import * as React from "react"
import { Plus, Star, StarOff, Trash2 } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Segmented } from "@/components/ui/segmented"
import { RowActions } from "@/components/ui/row-actions"
import { TableCheckbox } from "@/components/ui/table-checkbox"
import { notify } from "@/components/ui/notify"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

/*
  SavedViews — "All 14 · My tasks 3 · Waiting 0 · ★ Late LPOs · + Save view" (09 #3, 24 #4, 12 #5).
  Renders a useSavedViews() result (hooks/useSavedViews): presets and the views people saved, the
  open view highlighted, a ★ on the default view, a ⋯ menu for the open view (Open by default /
  Delete view) and "+ Save view" (name + "open by default"). Deleting offers Undo in a toast.

    const views = useSavedViews('execution', { presets, state, defaults, pristine, onApply });
    <SavedViews views={views} />          // or <FilterBar views={views} … />

  Props: views (the hook result), label ('Views', screen-reader name), canSave (default true),
  size ('sm' | 'md'), className, testId.
*/
function SaveViewPopover({ views, testId }) {
  const [open, setOpen] = React.useState(false)
  const [name, setName] = React.useState("")
  const [makeDefault, setMakeDefault] = React.useState(false)
  const nameId = React.useId()
  const defaultId = React.useId()

  const submit = (event) => {
    event.preventDefault()
    const view = views.save(name, { makeDefault })
    if (!view) return
    notify.success(`View “${view.label}” saved`)
    setOpen(false)
    setName("")
    setMakeDefault(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="text-ink-2" data-testid={testId ? `${testId}-save` : undefined}>
          <Plus aria-hidden="true" />
          Save view
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 rounded-xl border-line bg-surface p-3 text-ink shadow-overlay">
        <form onSubmit={submit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={nameId} className="text-[12px] font-semibold text-ink-2">View name</label>
            <input
              id={nameId}
              autoFocus
              value={name}
              maxLength={40}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Late LPOs this month"
              className="h-9 rounded-control border border-line bg-surface px-3 text-[13.5px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)]"
            />
            <p className="text-[12px] text-ink-3">Saves the current filters in this browser.</p>
          </div>
          <div className="flex items-center gap-2">
            <TableCheckbox id={defaultId} checked={makeDefault} onCheckedChange={setMakeDefault} />
            <label htmlFor={defaultId} className="text-[13px] text-ink-2">Open this view by default</label>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={!name.trim()}>Save view</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}

function SavedViews({ views, label = "Views", canSave = true, size = "sm", className, testId = "saved-views" }) {
  if (!views) return null
  const { activeView, defaultId } = views

  const options = views.views.map((view) => ({
    key: view.id,
    label: (
      <span className="inline-flex items-center gap-1">
        {view.id === defaultId ? <Star className="h-3 w-3 fill-current text-status-amber" aria-label="Default view" /> : null}
        {view.label}
      </span>
    ),
    count: view.count,
    title: view.id === defaultId ? `${view.label} — opens by default` : undefined,
    testId: `${testId}-${view.id}`,
  }))

  const removeView = (view) => {
    const wasDefault = view.id === defaultId
    views.remove(view.id)
    notify.success(`View “${view.label}” deleted`, { undo: () => views.restore(view, { wasDefault }) })
  }

  const menuItems = activeView ? [
    activeView.id === defaultId
      ? { label: "Don’t open by default", icon: StarOff, onSelect: () => views.setDefault(null) }
      : { label: "Open this view by default", icon: Star, onSelect: () => views.setDefault(activeView.id) },
    activeView.custom && { label: "Delete view", icon: Trash2, danger: true, onSelect: () => removeView(activeView) },
  ] : []

  return (
    <div className={cn("flex min-w-0 flex-wrap items-center gap-1.5", className)} data-testid={testId}>
      <Segmented
        ariaLabel={label}
        size={size}
        value={views.activeId}
        onChange={views.apply}
        options={options}
      />
      {views.isModified ? (
        <span className="text-[12px] font-semibold text-ink-3" data-testid={`${testId}-modified`}>Custom filters</span>
      ) : null}
      {activeView ? <RowActions label={`Options for view ${activeView.label}`} items={menuItems} data-testid={`${testId}-menu`} /> : null}
      {canSave ? <SaveViewPopover views={views} testId={testId} /> : null}
    </div>
  )
}

export { SavedViews }
